import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { Arn } from 'src/utils/types'
import { ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { getResourceTypeDisplayString } from 'src/app/shared/utils'
import { BaseIssue, IssueDeleter, RaisedIssue, RaisedIssueScope } from 'src/app/account/services/issue'
import { contentFingerprint, dedupeIssues, issueGroupKey } from 'src/app/account/services/issue-dedup'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApplicationResourceDto } from 'src/app/services/dtos'
import { Application } from '../../services/application.service'
import { IssueContentGroupComponent } from '../issue-view/issue-content-group/issue-content-group.component'

export interface ViewableIssue {
  issueId: string
  arn: Arn
  resourceName: string
  resourceType: ResourceType
  resourceVariant: ResourceVariant
  issue: BaseIssue
  scope: RaisedIssueScope
  deleter$: IssueDeleter
  timestamp: Date
}

// Enrich raw RaisedIssues with resource metadata (type, name, variant) from the app's resource list
export function makeViewableIssues(issues: Array<RaisedIssue>, resources: ApplicationResourceDto[]) {
  const viewableIssues = issues.map((raisedIssue) => {
    // Exact ARN match first, then prefix match for sub-resources (e.g. K8s workloads under an EKS cluster)
    let resource =
      resources.find((res) => res.arn === raisedIssue.arn) ||
      (raisedIssue.arn ? resources.find((res) => res.arn && raisedIssue.arn.startsWith(res.arn + '/')) : null)
    let resourceType: ResourceType = null
    let resourceVariant: ResourceVariant = null
    let resourceName: string = null
    if (resource) {
      resourceType = resource.type
      resourceVariant = resource.variant
      // Only use the parent resource's display name for exact matches
      if (resource.arn === raisedIssue.arn) {
        // Fall back to the physical id so untagged resources (e.g. an EC2 VPC with no Name
        // tag) still match the resources table, which stores the id when name is empty.
        resourceName = resource.source?.displayName || resource.source?.name || resource.source?.id || ''
      }
    }

    return {
      ...raisedIssue,
      resourceName,
      resourceType,
      resourceVariant,
      arn: raisedIssue.arn || '', // Ensure arn is never null or undefined
      timestamp: raisedIssue.timestamp,
    } as ViewableIssue
  })

  // Sort by resourceType first, then by arn, and finally by timestamp for issues without resource info
  return viewableIssues.sort((a, b) => {
    // First, compare resourceType
    const resourceTypeComparison = (a.resourceType || '').localeCompare(b.resourceType || '') || 0
    if (resourceTypeComparison !== 0) {
      return resourceTypeComparison
    }

    // If resourceType is the same, compare arn
    const arnComparison = (a.arn || '').localeCompare(b.arn || '')
    if (arnComparison !== 0) {
      return arnComparison
    }

    // If both resourceType and arn are the same or missing, fallback to timestamp comparison
    return a.timestamp?.getTime() - b.timestamp?.getTime()
  })
}

// Issues with identical body content (same fingerprint) are grouped into a ContentSubGroup.
// The representative's body is rendered once; the resource list shows all affected resources.
export interface ContentSubGroup {
  fingerprint: string
  representative: ViewableIssue // used to render shared body
  issues: ViewableIssue[] // all issues in sub-group
  expanded: boolean
}

export interface IssueGroup {
  issueType: string // internal key for tracking/deduplication
  displayName: string // human-readable label shown in the UI
  issues: ViewableIssue[]
  expanded: boolean
  resourceTypesLabel: string // distinct resource types, joined once here rather than per change-detection pass
  contentSubGroups: ContentSubGroup[]
}

const accordionSlide = trigger('accordionSlide', [
  state('collapsed', style({ height: '0px', paddingTop: '0', paddingBottom: '0' })),
  state('expanded', style({ height: '*', paddingTop: '*', paddingBottom: '*' })),
  transition('collapsed <=> expanded', animate('200ms ease-in-out')),
])

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'issues-view',
  templateUrl: './issues-view.component.html',
  styleUrls: ['./issues-view.component.scss'],
  animations: [accordionSlide],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class IssuesViewComponent implements OnInit, OnChanges {
  @Input() issues: Array<ViewableIssue> = null
  // tslint:disable-next-line:no-input-rename
  @Input('sync-pair') syncPair: SyncPair = null
  // tslint:disable-next-line:no-input-rename
  @Input('application') application: Application = null
  @Input() showToggleAll = true
  // When true, all groups and content-group accordions start expanded (used when
  // navigating from "Review N issues" link so all issues are visible immediately)
  @Input() expandOnLoad = false
  @Output() resourceClicked = new EventEmitter<string>() // emits ARN when user clicks a resource chip to navigate
  @ViewChildren(IssueContentGroupComponent) contentGroupChildren: QueryList<IssueContentGroupComponent>

  groupedIssues: IssueGroup[] = []

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.issues || changes.syncPair || changes.application) {
      this.computeGroups()
      this.cdr.markForCheck() // Ensure that the change detection runs when inputs change
    }
  }

  trackByFn(index: number, item: ViewableIssue) {
    return item.issueId
  }

  trackByGroup(index: number, group: IssueGroup) {
    return group.issueType
  }

  trackBySubGroup(index: number, subGroup: ContentSubGroup) {
    return subGroup.fingerprint
  }

  toggleGroup(group: IssueGroup) {
    group.expanded = !group.expanded
  }

  get multiIssueGroups(): IssueGroup[] {
    return this.groupedIssues.filter((g) => g.issues.length > 1)
  }

  // Both type-level groups AND individual content-group accordions must be expanded
  get allExpanded(): boolean {
    const groupsExpanded = this.multiIssueGroups.length === 0 || this.multiIssueGroups.every((g) => g.expanded)
    const allContentGroups = this.contentGroupChildren?.toArray() ?? []
    const allContentGroupsExpanded = allContentGroups.length === 0 || allContentGroups.every((c) => c.panelDisplayed)
    const hasContent = this.multiIssueGroups.length > 0 || allContentGroups.length > 0
    return hasContent && groupsExpanded && allContentGroupsExpanded
  }

  toggleAll() {
    const expand = !this.allExpanded
    for (const group of this.multiIssueGroups) {
      group.expanded = expand
    }
    this.contentGroupChildren?.forEach((c) => (expand ? c.expand() : c.collapse()))
  }

  private computeGroups() {
    if (!this.issues || this.issues.length === 0) {
      this.groupedIssues = []
      return
    }

    const groupMap = new Map<string, ViewableIssue[]>()
    const groupDisplayNames = new Map<string, string>()
    const groupOrder: string[] = []

    for (const issue of this.issues) {
      const key = issueGroupKey(issue)
      if (!groupMap.has(key)) {
        groupMap.set(key, [])
        groupDisplayNames.set(key, issue.issue.issueTypeDisplayString)
        groupOrder.push(key)
      }
      groupMap.get(key).push(issue)
    }

    // Preserve expanded state for existing groups and sub-groups
    const previousState = new Map<string, boolean>()
    const previousSubGroupState = new Map<string, boolean>()
    for (const group of this.groupedIssues) {
      previousState.set(group.issueType, group.expanded)
      for (const sg of group.contentSubGroups) {
        previousSubGroupState.set(`${group.issueType}\x00${sg.fingerprint}`, sg.expanded)
      }
    }

    this.groupedIssues = groupOrder.map((issueType) => {
      const wasExpanded = previousState.get(issueType)

      // Build content sub-groups within this type group
      const subGroupMap = new Map<string, ViewableIssue[]>()
      const subGroupOrder: string[] = []
      for (const issue of groupMap.get(issueType) ?? []) {
        const fp = contentFingerprint(issue)
        let bucket = subGroupMap.get(fp)
        if (!bucket) {
          bucket = []
          subGroupMap.set(fp, bucket)
          subGroupOrder.push(fp)
        }
        bucket.push(issue)
      }

      const contentSubGroups: ContentSubGroup[] = subGroupOrder.map((fp) => {
        // Redundant for issues that came through Application (already deduped at the
        // source), but kept so any caller passing raw issues still renders one row
        // per affected resource.
        const subIssues = dedupeIssues(subGroupMap.get(fp) ?? [])
        const sgKey = `${issueType}\x00${fp}`
        const wasSgExpanded = previousSubGroupState.get(sgKey)
        return {
          fingerprint: fp,
          representative: subIssues[0],
          issues: subIssues,
          expanded: wasSgExpanded !== undefined ? wasSgExpanded : this.expandOnLoad,
        }
      })

      // Derive the type-level list from the deduped sub-groups so the count badge and
      // resource-type chips match what's actually rendered.
      const issues = contentSubGroups.reduce((acc, sg) => acc.concat(sg.issues), [] as ViewableIssue[])
      const typeSet = new Set<string>()
      for (const issue of issues) {
        if (issue.resourceType) {
          typeSet.add(getResourceTypeDisplayString(issue.resourceType, issue.resourceVariant))
        }
      }

      return {
        issueType,
        displayName: groupDisplayNames.get(issueType) || issueType,
        issues,
        resourceTypesLabel: Array.from(typeSet).sort().join(', '),
        contentSubGroups,
        // Preserve previous state; default to expanded if expandOnLoad is set
        expanded: wasExpanded !== undefined ? wasExpanded : this.expandOnLoad,
      }
    })
  }
}
